import React, { useState, useEffect, useCallback, memo, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Loader, Search, Twitter, Users, GitCompare, RefreshCw, ChevronDown, Server, DollarSign, Activity, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatUsdValueCompact } from '../utils/chartHelpers';
import { truncateAddress } from '../utils/chartHelpers';
import type { SubnetMindshare, SubnetMindshareHistory } from '../types/mindshare';
import type { SubnetPool } from '../types/subnet';
import { subnetNames } from '../utils/subnetNames';
import { subnetDescriptions } from '../utils/subnetDescriptions';
import { getTwitterUsers } from '../services/twitterService';
import type { TwitterUser } from '../types/twitter';
import { collection, doc, getDoc, getDocs, query, orderBy, limit, Timestamp, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Line, Bar } from 'react-chartjs-2';

interface MindshareChartProps {
  mindshareData: SubnetMindshare[];
  loading: boolean;
  percentChanges?: Record<number, number>;
  onRefresh?: () => Promise<void>;
}

interface MindshareMoversData {
  name: string;
  netuid: string;
  percent_change: number;
  type: 'gainer' | 'loser';
}

type SquarePosition = {
  x: number;
  y: number;
  width: number;
  height: number;
  subnet: SubnetMindshare;
  rank: number;
  percentChange: number;
  twitterUser: TwitterUser | null;
};

export default function MindshareChart({ mindshareData, loading, percentChanges = {}, onRefresh }: MindshareChartProps) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [squares, setSquares] = useState<SquarePosition[]>([]);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [hoveredSquare, setHoveredSquare] = useState<SquarePosition | null>(null);
  const [itemCount, setItemCount] = useState<number>(10);
  const [twitterUsers, setTwitterUsers] = useState<Record<number, TwitterUser | null>>({});
  const [loadingTwitter, setLoadingTwitter] = useState(false);
  const [taoPrice, setTaoPrice] = useState<number | null>(null);
  const [totalEngagement, setTotalEngagement] = useState<number | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [priceHistory, setPriceHistory] = useState<Array<{timestamp: Date, price: number}>>([]);
  const [engagementHistory, setEngagementHistory] = useState<Array<{timestamp: Date, engagement: number}>>([]);
  const [mindshareMovers, setMindshareMovers] = useState<MindshareMoversData[]>([]);
  const [loadingMovers, setLoadingMovers] = useState(true);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Load Twitter users for subnets - memoized to prevent unnecessary rerenders
  const loadTwitterUsers = useCallback(async () => {
    try {
      setLoadingTwitter(true);
      const allTwitterUsers = await getTwitterUsers(100);
      const subnetTwitterMap: Record<number, TwitterUser | null> = {};
      
      // Match Twitter users to subnets
      mindshareData.forEach(subnet => {
        const subnetInfo = subnetDescriptions[subnet.netuid];
        if (!subnetInfo?.x) {
          subnetTwitterMap[subnet.netuid] = null;
          return;
        }
        
        // Extract username from X/Twitter URL
        const xUrlMatch = subnetInfo.x.match(/twitter\.com\/(\w+)|x\.com\/(\w+)/i);
        if (!xUrlMatch) {
          subnetTwitterMap[subnet.netuid] = null;
          return;
        }
        
        const xUsername = (xUrlMatch[1] || xUrlMatch[2] || '').toLowerCase();
        
        // Find matching Twitter user
        const matchingUser = allTwitterUsers.find(user => 
          user.username.toLowerCase() === xUsername
        );
        
        subnetTwitterMap[subnet.netuid] = matchingUser || null;
      });
      
      setTwitterUsers(subnetTwitterMap);
    } catch (err) {
      console.error('Failed to load Twitter users:', err);
    } finally {
      setLoadingTwitter(false);
    }
  }, [mindshareData]);

  // Load Twitter users on initial render
  useEffect(() => {
    if (mindshareData.length > 0 && Object.keys(twitterUsers).length === 0) {
      loadTwitterUsers();
    }
  }, [mindshareData, twitterUsers, loadTwitterUsers]);

  // Load engagement stats from Firestore
  useEffect(() => {
    const fetchEngagementStats = async () => {
      try {
        setLoadingStats(true);
        console.log('Fetching engagement stats...');
        
        // Fetch all documents from the engagement_stats collection
        const statsCollection = collection(db, 'engagement_stats');
        const statsQuery = query(statsCollection, orderBy('timestamp', 'desc'), limit(25));
        const statsSnapshot = await getDocs(statsQuery);
        
        if (statsSnapshot.empty) {
          console.log('No engagement stats documents found');
          return;
        }
        
        console.log(`Found ${statsSnapshot.size} engagement stats documents`);
        
        // Get the most recent document for current values
        const latestDoc = statsSnapshot.docs[0];
        const latestData = latestDoc.data();
        
        console.log('Latest engagement stats:', latestData);
        
        // Set current values
        setTaoPrice(latestData.tao_price || null);
        setTotalEngagement(latestData.total_engagement || null);
        
        // Process all documents for history
        const priceData: Array<{timestamp: Date, price: number}> = [];
        const engagementData: Array<{timestamp: Date, engagement: number}> = [];
        
        statsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          
          // Convert timestamp to Date
          let timestamp: Date;
          if (data.timestamp && typeof data.timestamp.toDate === 'function') {
            // Firestore Timestamp object
            timestamp = data.timestamp.toDate();
          } else if (data.timestamp) {
            // String or number timestamp
            timestamp = new Date(data.timestamp);
          } else {
            console.log(`Document ${doc.id} has no timestamp`);
            return;
          }
          
          // Add price data if available
          if (data.tao_price !== undefined) {
            priceData.push({
              timestamp,
              price: data.tao_price
            });
          }
          
          // Add engagement data if available
          if (data.total_engagement !== undefined) {
            engagementData.push({
              timestamp,
              engagement: data.total_engagement
            });
          }
        });
        
        console.log(`Processed ${priceData.length} price data points and ${engagementData.length} engagement data points`);
        
        // Sort by timestamp (oldest to newest)
        priceData.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        engagementData.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        
        setPriceHistory(priceData);
        setEngagementHistory(engagementData);
      } catch (error) {
        console.error('Error fetching engagement stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };
    
    fetchEngagementStats();
  }, []);

  // Load mindshare movers data
  useEffect(() => {
    const fetchMindshareMovers = async () => {
      try {
        setLoadingMovers(true);
        console.log('Fetching mindshare movers...');
        
        // Fetch documents from the mindshare_movers collection
        const moversCollection = collection(db, 'mindshare_movers');
        const moversSnapshot = await getDocs(moversCollection);
        
        if (moversSnapshot.empty) {
          console.log('No mindshare movers documents found');
          return;
        }
        
        console.log(`Found ${moversSnapshot.size} mindshare movers documents`);
        
        // Process documents
        const moversData: MindshareMoversData[] = [];
        
        moversSnapshot.docs.forEach(doc => {
          const data = doc.data();
          moversData.push({
            name: data.name || '',
            netuid: data.netuid || '',
            percent_change: data.percent_change || 0,
            type: data.type || 'gainer'
          });
        });
        
        console.log(`Processed ${moversData.length} mindshare movers`);
        
        // Sort gainers by percent_change (descending) and losers by percent_change (ascending)
        moversData.sort((a, b) => {
          if (a.type === 'gainer' && b.type === 'gainer') {
            return b.percent_change - a.percent_change;
          } else if (a.type === 'loser' && b.type === 'loser') {
            return a.percent_change - b.percent_change;
          } else if (a.type === 'gainer') {
            return -1; // Gainers first
          } else {
            return 1; // Losers second
          }
        });
        
        setMindshareMovers(moversData);
      } catch (error) {
        console.error('Error fetching mindshare movers:', error);
      } finally {
        setLoadingMovers(false);
      }
    };
    
    fetchMindshareMovers();
  }, []);

  // Calculate squares based on selected item count and container size
  const calculateSquares = useCallback(() => {
    if (!containerRef.current || mindshareData.length === 0) return;
    
    const container = containerRef.current;
    const width = container.clientWidth;
    
    const isMobile = window.innerWidth < 768;
    // Adjusted height for better balance
    const maxDesktopHeight = 500; // Adjusted from 600 to 500
    const aspectRatio = isMobile ? 1 : 0.7; // Adjusted from 0.8 to 0.7
    
    const calculatedHeight = width * aspectRatio;
    const height = isMobile ? calculatedHeight : Math.min(calculatedHeight, maxDesktopHeight);
    
    setContainerSize({ width, height });
    
    // Take top N subnets based on selected itemCount
    const topSubnets = mindshareData.slice(0, itemCount);
    
    // Sort by mindshare percentage (descending)
    const sortedSubnets = [...topSubnets].sort((a, b) => b.mindsharePercentage - a.mindsharePercentage);
    
    // Simple treemap layout algorithm
    const createTreemap = (
      items: SubnetMindshare[], 
      x: number, 
      y: number, 
      width: number, 
      height: number
    ): SquarePosition[] => {
      if (items.length === 0) return [];
      if (items.length === 1) {
        return [{
          x,
          y,
          width,
          height,
          subnet: items[0],
          rank: 1,
          percentChange: percentChanges[items[0].netuid] || 0,
          twitterUser: twitterUsers[items[0].netuid] || null
        }];
      }
      
      const totalMindshare = items.reduce((sum, item) => sum + item.mindsharePercentage, 0);
      const splitHorizontally = width > height;
      let currentSum = 0;
      let splitIndex = 0;
      
      for (let i = 0; i < items.length; i++) {
        currentSum += items[i].mindsharePercentage;
        if (currentSum >= totalMindshare / 2) {
          splitIndex = i;
          break;
        }
      }
      
      const group1 = items.slice(0, splitIndex + 1);
      const group2 = items.slice(splitIndex + 1);
      const ratio = group1.reduce((sum, item) => sum + item.mindsharePercentage, 0) / totalMindshare;
      
      if (splitHorizontally) {
        const group1Width = width * ratio;
        const group2Width = width - group1Width;
        return [
          ...createTreemap(group1, x, y, group1Width, height),
          ...createTreemap(group2, x + group1Width, y, group2Width, height)
        ];
      } else {
        const group1Height = height * ratio;
        const group2Height = height - group1Height;
        return [
          ...createTreemap(group1, x, y, width, group1Height),
          ...createTreemap(group2, x, y + group1Height, width, group2Height)
        ];
      }
    };
    
    const treemap = createTreemap(sortedSubnets, 0, 0, width, height);
    
    const rankedTreemap = treemap.map((square, index) => ({
      ...square,
      rank: sortedSubnets.findIndex(subnet => subnet.netuid === square.subnet.netuid) + 1,
      percentChange: percentChanges[square.subnet.netuid] || 0
    }));
    
    setSquares(rankedTreemap);
  }, [mindshareData, itemCount, twitterUsers, percentChanges]);

  // Set up resize observer for responsive layout
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Clean up previous observer if it exists
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }
    
    // Create new observer
    resizeObserverRef.current = new ResizeObserver(() => {
      calculateSquares();
    });
    
    // Start observing
    resizeObserverRef.current.observe(containerRef.current);
    
    // Initial calculation
    calculateSquares();
    
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [calculateSquares]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      calculateSquares();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateSquares]);

  const handleSubnetClick = useCallback((netuid: number) => {
    navigate(`/subnet/${netuid}`);
    
    // Reload the page to ensure fresh data
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, [navigate]);

  // Chart data for TAO price
  const priceChartData = {
    labels: priceHistory.map(item => item.timestamp),
    datasets: [
      {
        label: 'TAO Price',
        data: priceHistory.map(item => item.price),
        borderColor: 'rgba(6, 182, 212, 1)', // Cyan
        backgroundColor: 'rgba(6, 182, 212, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
      }
    ]
  };

  // Chart data for total engagement
  const engagementChartData = {
    labels: engagementHistory.map(item => item.timestamp),
    datasets: [
      {
        label: 'Total Engagement',
        data: engagementHistory.map(item => item.engagement),
        borderColor: 'rgba(6, 182, 212, 1)', // Changed to Cyan (blue) from red
        backgroundColor: 'rgba(6, 182, 212, 0.1)', // Changed to match the blue color
        fill: true,
        tension: 0.4,
        borderWidth: 2,
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { display: false },
      y: { display: false }
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    },
    elements: {
      point: { radius: 0 },
      line: { tension: 0.4 }
    }
  };

  // Prepare data for the gainers bar chart
  const prepareGainersChartData = () => {
    // Get only gainers
    const gainers = mindshareMovers
      .filter(item => item.type === 'gainer')
      .sort((a, b) => b.percent_change - a.percent_change)
      .slice(0, 5);
    
    return {
      labels: gainers.map(item => item.name),
      datasets: [
        {
          label: 'Percent Change',
          data: gainers.map(item => item.percent_change),
          backgroundColor: 'rgba(16, 185, 129, 0.8)', // Green for gainers
          borderColor: 'rgba(16, 185, 129, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  // Prepare data for the losers bar chart
  const prepareLosersChartData = () => {
    // Get only losers
    const losers = mindshareMovers
      .filter(item => item.type === 'loser')
      .sort((a, b) => a.percent_change - b.percent_change)
      .slice(0, 5);
    
    return {
      labels: losers.map(item => item.name),
      datasets: [
        {
          label: 'Percent Change',
          data: losers.map(item => Math.abs(item.percent_change)), // Use absolute values for better visualization
          backgroundColor: 'rgba(239, 68, 68, 0.8)', // Red for losers
          borderColor: 'rgba(239, 68, 68, 1)',
          borderWidth: 1
        }
      ]
    };
  };

  // Options for the movers bar charts
  const createMoversChartOptions = (type: 'gainer' | 'loser') => {
    return {
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.6)',
            callback: (value: number) => `${value.toFixed(1)}%`
          },
          border: {
            display: false
          }
        },
        y: {
          grid: {
            display: false
          },
          ticks: {
            color: 'rgba(255, 255, 255, 0.8)',
            font: {
              size: window.innerWidth < 768 ? 10 : 11
            }
          },
          border: {
            display: false
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'rgba(255, 255, 255, 0.8)',
          bodyColor: 'rgba(255, 255, 255, 0.8)',
          callbacks: {
            label: (context: any) => {
              const value = context.raw;
              const index = context.dataIndex;
              
              // Find the corresponding mover
              const movers = mindshareMovers.filter(m => m.type === type);
              if (index >= movers.length) return '';
              
              const mover = movers[index];
              
              return [
                `Change: ${type === 'gainer' ? '+' : '-'}${value.toFixed(2)}%`,
                `Subnet: ${mover.netuid}`,
                'Click to view subnet details'
              ];
            }
          }
        }
      },
      elements: {
        bar: {
          borderRadius: 4,
        }
      },
      onClick: (event: any, elements: any[]) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          const movers = mindshareMovers.filter(m => m.type === type);
          if (index < movers.length) {
            const mover = movers[index];
            if (mover && mover.netuid) {
              navigate(`/subnet/${mover.netuid}`);
              
              // Reload the page to ensure fresh data
              setTimeout(() => {
                window.location.reload();
              }, 100);
            }
          }
        }
      },
      onHover: (event: any, elements: any[]) => {
        event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    };
  };

  if (loading || loadingTwitter) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading mindshare data...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Main content grid - made more spacious but balanced */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Stats and Movers - 1/3 width on desktop */}
        <div className="space-y-4">
          {/* Stats Grid - 2 columns */}
          <div className="grid grid-cols-2 gap-3">
            {/* TAO Price Box */}
            <div className="bg-black/20 backdrop-blur-sm rounded-lg p-3 border border-white/5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <DollarSign className="w-4 h-4 text-cyan-400 mr-1" />
                  <h3 className="text-sm text-gray-400">Total Subnet Price</h3>
                </div>
              </div>
              
              {loadingStats ? (
                <div className="h-5 w-16 bg-gray-700/50 animate-pulse rounded mb-1"></div>
              ) : (
                <p className="text-lg font-bold mb-1">{taoPrice ? `${taoPrice.toFixed(2)} τ` : 'N/A'}</p>
              )}
              
              {/* Price Chart */}
              <div className="h-[50px]">
                {loadingStats ? (
                  <div className="h-full w-full bg-gray-700/50 animate-pulse rounded"></div>
                ) : priceHistory.length > 0 ? (
                  <Line data={priceChartData} options={chartOptions} />
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 text-xs">
                    No data
                  </div>
                )}
              </div>
            </div>
            
            {/* Total Engagement Box */}
            <div className="bg-black/20 backdrop-blur-sm rounded-lg p-3 border border-white/5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <Activity className="w-4 h-4 text-cyan-400 mr-1" />
                  <h3 className="text-sm text-gray-400">Total Engagement</h3>
                </div>
              </div>
              
              {loadingStats ? (
                <div className="h-5 w-16 bg-gray-700/50 animate-pulse rounded mb-1"></div>
              ) : (
                <p className="text-lg font-bold mb-1">{totalEngagement?.toLocaleString() || 'N/A'}</p>
              )}
              
              {/* Engagement Chart */}
              <div className="h-[50px]">
                {loadingStats ? (
                  <div className="h-full w-full bg-gray-700/50 animate-pulse rounded"></div>
                ) : engagementHistory.length > 0 ? (
                  <Line data={engagementChartData} options={chartOptions} />
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 text-xs">
                    No data
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Gainers Chart */}
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-3 border border-white/5">
            <div className="flex items-center mb-2">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <h3 className="text-sm font-semibold">Top Gainers</h3>
            </div>
            
            <div style={{ height: "150px" }}>
              {loadingMovers ? (
                <div className="h-full flex items-center justify-center">
                  <Loader className="w-4 h-4 text-cyan-400 animate-spin" />
                </div>
              ) : mindshareMovers.filter(m => m.type === 'gainer').length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-xs">
                  No gainers data
                </div>
              ) : (
                <Bar 
                  data={prepareGainersChartData()} 
                  options={createMoversChartOptions('gainer')}
                />
              )}
            </div>
          </div>
          
          {/* Losers Chart */}
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-3 border border-white/5">
            <div className="flex items-center mb-2">
              <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
              <h3 className="text-sm font-semibold">Top Losers</h3>
            </div>
            
            <div style={{ height: "150px" }}>
              {loadingMovers ? (
                <div className="h-full flex items-center justify-center">
                  <Loader className="w-4 h-4 text-cyan-400 animate-spin" />
                </div>
              ) : mindshareMovers.filter(m => m.type === 'loser').length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-xs">
                  No losers data
                </div>
              ) : (
                <Bar 
                  data={prepareLosersChartData()} 
                  options={createMoversChartOptions('loser')}
                />
              )}
            </div>
          </div>
        </div>

        {/* Mindshare Visualization - 2/3 width on desktop */}
        <div className="md:col-span-2">
          <div className="bg-black/20 backdrop-blur-sm rounded-lg p-3 border border-white/5 h-full">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Brain className="w-5 h-5 text-cyan-400 mr-2" />
                <h2 className="text-base font-bold">Subnet Mindshare Visualization</h2>
              </div>
              
              <div className="relative inline-block">
                <select
                  value={itemCount}
                  onChange={(e) => setItemCount(Number(e.target.value))}
                  className="bg-black/20 border border-white/10 rounded-lg px-2 py-1 pr-6 text-sm appearance-none cursor-pointer hover:bg-black/30 transition-colors focus:outline-none focus:ring-1 focus:ring-cyan-600"
                >
                  <option value={10}>Show 10</option>
                  <option value={15}>Show 15</option>
                  <option value={20}>Show 20</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            
            <div 
              ref={containerRef} 
              className="relative bg-black/20 backdrop-blur-sm rounded-lg border border-white/10"
              style={{ height: `${containerSize.height}px`, minHeight: '450px' }}
            >
              {squares.map((square) => {
                const opacity = 0.2 + (square.subnet.mindsharePercentage / 100) * 0.3;
                
                // Special case for subnet 14 - use the provided profile image
                const isSubnet14 = square.subnet.netuid === 14;
                const profileImageUrl = isSubnet14 
                  ? "https://pbs.twimg.com/profile_images/1922656948530577409/mLnpdbFj_400x400.jpg"
                  : square.twitterUser?.profile_image_url.replace('_normal', '');
                
                const hasProfileImage = isSubnet14 || !!profileImageUrl;
                
                return (
                  <div
                    key={square.subnet.netuid}
                    className={`absolute flex items-center justify-center transition-all duration-300 cursor-pointer
                      ${hoveredSquare === square ? 'ring-2 ring-white/30 z-50' : 'z-10'}`}
                    style={{
                      left: square.x,
                      top: square.y,
                      width: square.width,
                      height: square.height,
                      backgroundColor: hasProfileImage ? `rgba(0, 0, 0, ${opacity})` : `rgba(30, 30, 30, ${opacity})`,
                      backgroundImage: hasProfileImage ? `url(${isSubnet14 ? "https://pbs.twimg.com/profile_images/1922656948530577409/mLnpdbFj_400x400.jpg" : profileImageUrl})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      transform: hoveredSquare === square ? 'scale(1.02)' : 'scale(1)',
                      border: '1px solid rgba(0, 0, 0, 0.2)'
                    }}
                    onMouseEnter={() => setHoveredSquare(square)}
                    onMouseLeave={() => setHoveredSquare(null)}
                    onClick={() => handleSubnetClick(square.subnet.netuid)}
                  >
                    {!hasProfileImage && (
                      <div className="w-1/3 h-1/3 rounded-full bg-black/40 flex items-center justify-center">
                        {subnetDescriptions[square.subnet.netuid]?.x ? (
                          <Twitter className="w-1/2 h-1/2 text-white/70" />
                        ) : (
                          <Server className="w-1/2 h-1/2 text-white/70" />
                        )}
                      </div>
                    )}
                    
                    <div className="absolute bottom-0 left-0 right-0 z-20">
                      <div className="text-center pb-2 px-2 bg-black/70 backdrop-blur-sm">
                        <div className="text-white font-bold text-sm truncate" style={{ textShadow: '0px 2px 4px rgba(0,0,0,0.9)' }}>
                          {square.subnet.name} - SN{square.subnet.netuid}
                        </div>
                        <div className="flex items-center justify-center text-white/90 text-xs truncate" style={{ textShadow: '0px 2px 4px rgba(0,0,0,0.9)' }}>
                          <span>Rank {square.rank} - {square.subnet.mindsharePercentage.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                    
                    {hoveredSquare === square && (
                      <div className="absolute -top-14 left-1/2 transform -translate-x-1/2 bg-black/90 backdrop-blur-sm rounded-lg p-2 text-white text-sm whitespace-nowrap z-50 border border-white/20 shadow-lg">
                        <div className="font-bold">{square.subnet.mindsharePercentage.toFixed(1)}% Mindshare</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { MindshareChart }